import { useEffect, useState } from "react";
import { Music, X } from "lucide-react";
import {
  useSessionSettingsQuery,
  useUpdateSessionSettingsMutation,
} from "../../apollo/sessionSettings";

// Mirrors RECORDING_NOTICE_FALLBACK_TEXT in hd-api/helpers/recording_notice.py —
// shown as the placeholder so tenants see what plays when they don't customize.
const DEFAULT_NOTICE_TEXT =
  "This call may be recorded for the convenience of your provider and you. " +
  "By continuing, you acknowledge this notice.";

const NOTICE_TEXT_MAX_LENGTH = 500;
const MAX_AUDIO_BYTES = 1024 * 1024; // 1MB, matches the upload endpoint

// ── Audio Input Component ─────────────────────────────────────

interface NoticeAudioInputProps {
  audioUrl: string | null;
  onUpdate: (url: string | null, storageKey: string | null) => void;
}

function NoticeAudioInput({ audioUrl, onUpdate }: NoticeAudioInputProps) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > MAX_AUDIO_BYTES) {
      setError("File too large. Maximum size is 1 MB.");
      e.target.value = "";
      return;
    }

    setUploading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append("file", file);

      const res = await fetch("/api/session-settings/phone-notice/upload", {
        method: "POST",
        credentials: "include",
        body: formData,
      });

      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as
          | { error?: string }
          | null;
        throw new Error(body?.error ?? "Upload failed");
      }

      const data = (await res.json()) as { cdn_url: string; storage_key: string };
      onUpdate(data.cdn_url, data.storage_key);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const handleRemove = () => {
    onUpdate(null, null);
  };

  return (
    <div>
      {audioUrl ? (
        <div className="hair relative rounded-md border bg-paper p-4 pr-12">
          <audio controls src={audioUrl} className="w-full" preload="none" />
          <button
            type="button"
            onClick={handleRemove}
            aria-label="Remove audio"
            className="absolute right-2 top-2 flex h-7 w-7 cursor-pointer items-center justify-center rounded-full bg-paper-hi/95 text-ink-3 shadow-md hover:text-rust"
          >
            <X size={14} strokeWidth={2} />
          </button>
        </div>
      ) : (
        <label
          className={`hair flex cursor-pointer flex-col items-center justify-center gap-1 rounded-md border border-dashed bg-paper px-4 py-8 text-center transition hover:bg-paper-2 ${
            uploading ? "opacity-60" : ""
          }`}
        >
          <Music size={20} strokeWidth={1.6} className="text-ink-4" />
          <div className="text-[12.5px] font-medium text-ink-3">
            {uploading ? "Uploading…" : "Upload recording"}
          </div>
          <div className="text-[11px] text-ink-5">MP3 • ≤1 MB</div>
          <input
            type="file"
            accept="audio/mpeg,.mp3"
            hidden
            onChange={handleUpload}
            disabled={uploading}
          />
        </label>
      )}
      {error && <div className="mt-2 text-[11.5px] text-rust">{error}</div>}
    </div>
  );
}

// ── Main Phone Bridge Page Component ───────────────────────────

export function PhoneBridgePage() {
  const { data, loading, error } = useSessionSettingsQuery();
  const [updateSessionSettings, { error: saveError }] =
    useUpdateSessionSettingsMutation();

  const settings = data?.sessionSettings;
  const savedText = settings?.phoneNoticeText ?? "";
  const audioUrl = settings?.phoneNoticeAudioUrl || null;

  const [noticeText, setNoticeText] = useState(savedText);
  const [textSaved, setTextSaved] = useState(false);

  // Sync the textarea when the saved value arrives/changes elsewhere
  useEffect(() => {
    setNoticeText(savedText);
  }, [savedText]);

  const textDirty = noticeText.trim() !== savedText.trim();

  const handleSaveText = () => {
    void updateSessionSettings({
      variables: { input: { phoneNoticeText: noticeText.trim() || null } },
    }).then(({ data: result }) => {
      if (result?.updateSessionSettings) {
        setTextSaved(true);
        setTimeout(() => setTextSaved(false), 2000);
      }
    });
  };

  const handleAudioUpdate = (url: string | null, storageKey: string | null) => {
    void updateSessionSettings({
      variables: {
        input: { phoneNoticeAudioUrl: url, phoneNoticeAudioKey: storageKey },
      },
    });
  };

  const effectiveNotice = audioUrl
    ? "your uploaded recording"
    : savedText.trim()
      ? "your custom message, read aloud by text-to-speech"
      : "the default notice, read aloud by text-to-speech";

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-[13px] text-ink-5">Loading phone bridge settings…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-md bg-rust-s/40 px-3 py-2 text-[12.5px] text-rust">
        Failed to load phone bridge settings: {error.message}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-12 gap-3">
      <section className="col-span-12 lg:col-span-8">
        <div className="mb-8">
          <h1 className="font-display text-[40px] font-medium leading-[1.05] tracking-tight">
            Phone bridge
          </h1>
          <p className="mt-2 max-w-xl text-[14.5px] leading-relaxed text-ink-3">
            Customize the recording notice played to participants who join a
            session by phone, before they are connected.
          </p>
        </div>

        <div className="space-y-8">
          {/* Custom audio section */}
          <section className="shadow-card rounded-lg bg-paper-hi">
            <div className="hair-b border-b px-7 py-4">
              <h2 className="text-[14px] font-semibold text-ink">
                Notice recording
              </h2>
            </div>
            <div className="p-7">
              <p className="mb-4 max-w-lg text-[13px] text-ink-3">
                Upload your own recording of the notice. When set, it is played
                to phone callers instead of the spoken message below.
              </p>
              <NoticeAudioInput audioUrl={audioUrl} onUpdate={handleAudioUpdate} />
              {saveError && (
                <div className="mt-3 text-[11.5px] text-rust">
                  {saveError.message}
                </div>
              )}
            </div>
          </section>

          {/* Custom TTS text section */}
          <section className="shadow-card rounded-lg bg-paper-hi">
            <div className="hair-b border-b px-7 py-4">
              <h2 className="text-[14px] font-semibold text-ink">
                Notice message
              </h2>
            </div>
            <div className="p-7">
              <p className="mb-4 max-w-lg text-[13px] text-ink-3">
                The message read aloud to phone callers when no recording is
                uploaded. Leave blank to use the default notice.
              </p>
              <textarea
                value={noticeText}
                onChange={(e) =>
                  setNoticeText(e.target.value.slice(0, NOTICE_TEXT_MAX_LENGTH))
                }
                placeholder={DEFAULT_NOTICE_TEXT}
                rows={4}
                className="hair w-full resize-y rounded-md border bg-paper px-3 py-2 text-[13px] leading-relaxed text-ink placeholder:text-ink-5 focus:border-tide focus:outline-none"
              />
              <div className="mt-2 flex items-center justify-between">
                <span className="text-[11px] text-ink-5">
                  {noticeText.length}/{NOTICE_TEXT_MAX_LENGTH}
                </span>
                <div className="flex items-center gap-3">
                  {textSaved && (
                    <span className="text-[11.5px] text-sage">Saved</span>
                  )}
                  <button
                    type="button"
                    onClick={handleSaveText}
                    disabled={!textDirty}
                    className={`rounded-md px-4 py-1.5 text-[12.5px] font-medium ${
                      textDirty
                        ? "bg-ink text-paper-hi hover:bg-ink-2"
                        : "hair border bg-paper text-ink-5"
                    }`}
                  >
                    Save
                  </button>
                </div>
              </div>
              {saveError && (
                <div className="mt-3 text-[11.5px] text-rust">
                  {saveError.message}
                </div>
              )}
            </div>
          </section>
        </div>
      </section>

      {/* What callers hear */}
      <div className="col-span-12 lg:col-span-4">
        <div className="shadow-card rounded-lg bg-paper-hi p-5">
          <h3 className="text-[13px] font-semibold text-ink">
            What callers will hear
          </h3>
          <p className="mt-2 text-[12.5px] leading-relaxed text-ink-3">
            Phone participants currently hear {effectiveNotice} before joining
            the session.
          </p>
          <p className="mt-3 text-[11.5px] leading-relaxed text-ink-5">
            Priority: uploaded recording, then custom message, then the default
            notice. Uploaded recordings also play in the browser notice screen
            before participants join by video.
          </p>
        </div>
      </div>
    </div>
  );
}
